import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  useToast
} from "@chakra-ui/react";
import {
  default as React,
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { useUsersApi } from "src/api";
import { useMissionsApi } from "src/api/missions/missions.api";
import { SolidButton } from "src/components/button";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { theme } from "src/components/theme";

export interface FormProps {
  name: string
  email: string
  password: string
  admin: boolean
  missionId: string
}

export const CreateUserPage: React.FC = () => {
  const history = useHistory();
  const toast = useToast();

  const {
    register, handleSubmit, setValue
  } = useForm<FormProps>();

  const [ createUserRes, createUserReq ] = useUsersApi("CREATE_USER");
  const [ missionsRes, missionsReq ] = useMissionsApi("GET_MISSIONS");
  const [ active, setActive ] = useState(true);

  useEffect(() => {
    missionsReq({});
  }, [ missionsReq ]);

  useEffect(() => {
    const general = missionsRes.data?.results.find(mission => mission.isGeneral);

    if (general) {
      setValue("missionId", general._id);
    }
  }, [ missionsRes.data, setValue ]);

  const onSubmit = useCallback((data: FormProps) => {
    createUserReq({
      name: data.name,
      email: data.email,
      password: data.password,
      admin: data.admin,
      missionId: data.missionId,
      active
    });
  }, [ active, createUserReq ]);

  useEffect(() => {
    if (createUserRes.error) {
      toast({
        status: "error",
        title: "Error creating user",
        description: createUserRes.error.details
      });
    }

    if (createUserRes.data) {
      toast({
        status: "success",
        title: "User created"

      });
      history.push("/users");
    }
  }, [
    createUserRes,
    toast,
    history
  ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          Create user
        </Text>

        <Text as="p">
          Users do not automatically receive an invitation to join, so you'll need to send invites separately.
        </Text>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="row"
          >
            <Box
              display="flex"
              flexDirection="column"
              width="50%"
            >
              <FormControl
                paddingY={5}
                id="name"
              >
                <FormLabel>
                  Name
                </FormLabel>

                <Input
                  type="text"
                  required
                  {...register("name")}
                />
              </FormControl>

              <FormControl
                paddingY={5}
                id="email"
              >
                <FormLabel>
                  Email
                </FormLabel>

                <Input
                  type="email"
                  required
                  {...register("email")}
                />
              </FormControl>

              <FormControl
                paddingY={5}
                id="password"
              >
                <FormLabel>
                  Password
                </FormLabel>

                <Input
                  type="text"
                  required
                  {...register("password")}
                />
              </FormControl>

              <FormControl
                paddingY={5}
                id="mission"
              >
                <FormLabel>
                  Mission
                </FormLabel>

                <Select
                  placeholder="Select a mission"
                  {...register("missionId", { required: true })}
                >
                  {(missionsRes.data?.results || [])
                    .slice()
                    .sort((left, right) => left.name.localeCompare(right.name, undefined, {
                      numeric: true,
                      sensitivity: "base"
                    }))
                    .map(mission => (
                      <option
                        key={mission._id}
                        value={mission._id}
                      >
                        {mission.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl
                paddingY={5}
                id="active"
                display="flex"
                alignItems="center"
              >
                <FormLabel mb={0}>
                  Active
                </FormLabel>

                <Switch
                  isChecked={active}
                  onChange={event => setActive(event.target.checked)}
                />
              </FormControl>

              <FormControl
                paddingY={5}
                id="admin"
              >
                <Checkbox
                  borderColor={theme.colors.lightGrey}
                  marginRight="5px"
                  type="checkbox"
                  {...register("admin")}
                >
                  Make this user an admin
                </Checkbox>

                <Box marginTop="1rem">
                  <Text as="small">
                    Admins can create and delete users, and see all sessions.
                  </Text>
                </Box>
              </FormControl>

              <SolidButton
                marginTop="2rem"
                type="submit"
              >
                Create user
              </SolidButton>
            </Box>

          </Box>
        </form>

      </Box>

    </Box>
  );
};
